import React, { createContext, useEffect, useContext, useMemo } from 'react';

import { api } from '@shared/api';
import { LoanApplication } from '@shared/api/@types/loans';
import { GetLoanApplicationsData } from '@shared/api/lib/generated/loansPublicApi.generated';
import env from '@shared/config';
import { Big } from '@shared/safe-big';
import { UserStore } from '@shared/store';

import { useAvo } from '@hooks/Avo/useAvo';

import { useInterpret, useSelector } from '@xstate/react';
import { DateTime } from 'luxon';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';
import { assign, InterpreterFrom, Subscribable, DoneInvokeEvent } from 'xstate';

import {
  loanApplicationMachine,
  Context as LoanApplicationContextType,
  Event as LoanApplicationEventType,
} from './LoanApplication.machine';
import { LoanApplicationStepId } from './types/LoanApplication.types';

export const LoanApplicationContext = createContext<{
  loanApplicationService: InterpreterFrom<typeof loanApplicationMachine>;
}>({
  loanApplicationService: {} as InterpreterFrom<typeof loanApplicationMachine>,
});

/**
 * Utility function to convert loan application string numbers into bigs
 * in lieu of doing proper DTOs lol
 */
function bigifyLoanApplication(apiApp: GetLoanApplicationsData[number]): Partial<LoanApplication> {
  if (!apiApp) return {};
  return {
    ...apiApp,
    borrowPrincipalAmount: apiApp.borrowPrincipalAmount != null ? Big(apiApp.borrowPrincipalAmount) : undefined,
    expectedMonthlyExpenses: apiApp.expectedMonthlyExpenses != null ? Big(apiApp.expectedMonthlyExpenses) : undefined,
    expectedMonthlyIncome: apiApp.expectedMonthlyIncome != null ? Big(apiApp.expectedMonthlyIncome) : undefined,
    expectedTotalExistingCreditLimits:
      apiApp.expectedTotalExistingCreditLimits != null ? Big(apiApp.expectedTotalExistingCreditLimits) : undefined,
    monthlyExpenses: apiApp.monthlyExpenses != null ? Big(apiApp.monthlyExpenses) : undefined,
    monthlyIncome: apiApp.monthlyIncome != null ? Big(apiApp.monthlyIncome) : undefined,
    securityAmount: apiApp.securityAmount != null ? Big(apiApp.securityAmount) : undefined,
    totalExistingCreditLimits:
      apiApp.totalExistingCreditLimits != null ? Big(apiApp.totalExistingCreditLimits) : undefined,
    createdAt: apiApp.createdAt != null ? DateTime.fromMillis(apiApp.createdAt) : undefined,
    updatedAt: apiApp.updatedAt != null ? DateTime.fromMillis(apiApp.updatedAt) : undefined,
    submittedAt: apiApp.submittedAt != null ? DateTime.fromMillis(apiApp.submittedAt) : null,
  };
}

const LoanApplicationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { navigate } = useNavigateRoute();
  const avo = useAvo();

  const userProfile = useMemo(() => UserStore.useUserStore.userProfile, []);
  const { isNZ } = UserStore.useUserStore;

  const loanApplicationService = useInterpret(loanApplicationMachine, {
    devTools: env.ENABLE_XSTATE_INSPECT,
    context: {
      applicationData: {},
      mobileCodeLoanApplicationData: { userInputCode: '', error: '' },
      completedSteps: {},
    },
    actions: {
      prefillLoanApplicationData: assign((context, event) => {
        const fetchedData = (event as DoneInvokeEvent<Partial<LoanApplication>>).data;
        return {
          applicationData: { ...context.applicationData, ...fetchedData },
        };
      }),
      completeStep: assign((context, event) => {
        const specificEvent = event as Extract<LoanApplicationEventType, { type: 'COMPLETE_STEP' }>;
        return {
          completedSteps: {
            ...context.completedSteps,
            [specificEvent.stepId]: true,
          },
        };
      }),
      redirectToDashboard: () => {
        navigate(NavigationURLs.Dashboard);
      },
      syncDataWithDB: (context: LoanApplicationContextType, _event: LoanApplicationEventType) => {
        if (!('data' in _event) || !_event.data) return;
        console.log(`Syncing data with DB (${_event.type}):`, context.applicationData);
        api.endpoints.syncLoanApplication({
          data: { ..._event.data, loanOfferId: context.applicationData.loanOfferId },
        });
      },
      assignLoanApplicationData: assign((context, event) => ({
        applicationData: {
          ...context.applicationData,
          ...event.data,
        },
      })),
    },
    services: {
      fetchLoanApplicationData: async () => {
        const loanApplications = await api.endpoints.getLoanApplications().then((res) => res.data);
        if (!loanApplications || loanApplications.length === 0) {
          return {} as Partial<LoanApplication>;
        }
        const inProgressApplication = loanApplications.find((application) => application.status === 'DRAFT');
        if (inProgressApplication) {
          const bigifiedApplication = bigifyLoanApplication(inProgressApplication) as Partial<LoanApplication>;

          // Check for missing fields
          if (
            !bigifiedApplication.loanOfferId ||
            !bigifiedApplication.securityAssetCode ||
            !bigifiedApplication.borrowAssetCode
          ) {
            const { data: loanOffers } = await api.endpoints.getLoanOffers();
            const loanOffer = loanOffers.length > 0 ? loanOffers[0] : undefined;
            return {
              ...bigifiedApplication,
              loanOfferId: bigifiedApplication.loanOfferId ?? loanOffer?.id,
              securityAssetCode: bigifiedApplication.securityAssetCode ?? loanOffer?.cryptoCurrency,
              borrowAssetCode: bigifiedApplication.borrowAssetCode ?? loanOffer?.fiatCurrency,
            } as Partial<LoanApplication>;
          }
          return bigifiedApplication;
        }

        // Note: at the moment assuiming the backend will return the right type (asset/type)
        const { data: loanOffers } = await api.endpoints.getLoanOffers();
        const loanOffer = loanOffers.length > 0 ? loanOffers[0] : undefined;

        return {
          loanOfferId: loanOffer?.id,
          securityAssetCode: loanOffer?.cryptoCurrency,
          borrowAssetCode: loanOffer?.fiatCurrency,
        } as Partial<LoanApplication>;
      },
    },
  });

  useEffect(() => {
    loanApplicationService.onTransition((state, event) => {
      if (event.type === 'NEXT' || event.type === 'SUBMIT') {
        const stepId = state.history?.toStrings()?.slice(0, 2).slice(-1)[0] as LoanApplicationStepId;
        if (stepId && !state.context.completedSteps[stepId]) {
          loanApplicationService.send({ type: 'COMPLETE_STEP', stepId });
        }
      }
    });

    loanApplicationService.onStop(() => {
      const stepId = loanApplicationService.state.toStrings()?.slice(0, 2).slice(-1)[0];
      avo.uxFlowExited({
        screen: NavigationURLs.LoansApplication,
        stepId,
        featureName: 'loanApplication',
        modalName: null,
      });
    });
  }, [avo, loanApplicationService, userProfile, isNZ]);

  const value = useMemo(
    () => ({
      loanApplicationService,
    }),
    [loanApplicationService],
  );

  return <LoanApplicationContext.Provider value={value}>{children}</LoanApplicationContext.Provider>;
};

export const useLoanApplicationService = () => {
  const { loanApplicationService } = useContext(LoanApplicationContext);
  return loanApplicationService;
};

export { LoanApplicationProvider };

export const useLoanApplicationSelector = <
  T,
  TEmitted = InterpreterFrom<typeof loanApplicationMachine> extends Subscribable<infer Emitted> ? Emitted : never,
>(
  selector: (emitted: TEmitted) => T,
) => {
  const loanApplicationService = useLoanApplicationService();
  const value = useSelector(loanApplicationService, selector);

  return value;
};
